import { AfterViewInit, Component, OnInit, ViewChild, inject, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import {
  AppsConversionConfigService,
  AppsConversionEmployee,
} from '@lmc-app/core/services/apps-conversion-config.service';
import { SnackbarConstants } from '@lmc-app/shared/constants/snackbar.constant';

@Component({
  selector: 'app-apps-conversion-settings',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatCardModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatSlideToggleModule,
    MatSnackBarModule,
    MatSortModule,
    MatTableModule,
  ],
  templateUrl: './apps-conversion-settings.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./apps-conversion-settings.component.scss'],
})
export class AppsConversionSettingsComponent implements OnInit, AfterViewInit {
  private readonly _AppsConversionConfigService = inject(AppsConversionConfigService);
  private readonly _SnackBar = inject(MatSnackBar);

  @ViewChild(MatSort) sort: MatSort;

  displayedColumns: string[] = ['select', 'full_name', 'position'];
  dataSource = new MatTableDataSource<AppsConversionEmployee>([]);

  selectedIds = new Set<number>();
  searchText = '';
  selectedOnly = false;
  isLoading = false;
  isSaving = false;

  ngOnInit(): void {
    this.dataSource.filterPredicate = (employee, filter) => {
      if (this.selectedOnly && !this.selectedIds.has(employee.employeeID)) return false;
      const search = filter.trim();
      return [employee.full_name, employee.position]
        .some((value) => (value || '').toLowerCase().includes(search));
    };
    this.loadConfig();
  }

  ngAfterViewInit(): void {
    this.dataSource.sort = this.sort;
  }

  loadConfig(): void {
    this.isLoading = true;
    this._AppsConversionConfigService.getConfig().subscribe({
      next: (res) => {
        this.selectedIds = new Set(res.data.employeeIds);
        this.dataSource.data = res.data.employees;
        this.isLoading = false;
      },
      error: (error) => {
        this.isLoading = false;
        this.showMessage(error.error?.message || 'Failed to load Apps Conversion dashboard settings');
      },
    });
  }

  applyFilter(): void {
    // A non-empty filter string is needed for the predicate to run
    this.dataSource.filter = this.searchText.trim().toLowerCase() || ' ';
  }

  isSelected(employee: AppsConversionEmployee): boolean {
    return this.selectedIds.has(employee.employeeID);
  }

  toggle(employee: AppsConversionEmployee): void {
    if (this.selectedIds.has(employee.employeeID)) {
      this.selectedIds.delete(employee.employeeID);
    } else {
      this.selectedIds.add(employee.employeeID);
    }
  }

  isAllVisibleSelected(): boolean {
    const visible = this.dataSource.filteredData;
    return visible.length > 0 && visible.every((employee) => this.isSelected(employee));
  }

  isSomeVisibleSelected(): boolean {
    const visible = this.dataSource.filteredData;
    return visible.some((employee) => this.isSelected(employee)) && !this.isAllVisibleSelected();
  }

  toggleAllVisible(): void {
    const selectAll = !this.isAllVisibleSelected();
    this.dataSource.filteredData.forEach((employee) => {
      if (selectAll) {
        this.selectedIds.add(employee.employeeID);
      } else {
        this.selectedIds.delete(employee.employeeID);
      }
    });
  }

  save(): void {
    this.isSaving = true;
    this._AppsConversionConfigService.updateConfig([...this.selectedIds]).subscribe({
      next: (res) => {
        this.isSaving = false;
        this.showMessage(res.message);
      },
      error: (error) => {
        this.isSaving = false;
        this.showMessage(error.error?.message || 'Failed to save Apps Conversion dashboard employees');
      },
    });
  }

  private showMessage(message: string): void {
    this._SnackBar.open(message, SnackbarConstants.ACTION_DISMISS, SnackbarConstants.DURATION_DISMISS_SHORT);
  }
}
